<template>
  <div v-if="rules.length" class="recharge-campaign-tip">
    <div class="recharge-campaign-tip__head">
      <span class="recharge-campaign-tip__title">🎁 {{ campaign.title }} · 充值满赠</span>
      <span class="recharge-campaign-tip__badge">最高赠 {{ formatNum(maxBonus) }}</span>
    </div>
    <div class="recharge-campaign-tip__time">活动时间：{{ campaign.start_at }} 至 {{ campaign.end_at }}</div>
    <div class="recharge-campaign-tip__grid">
      <div v-for="rule in rules" :key="rule.threshold" class="recharge-campaign-tip__tier" :class="{ 'recharge-campaign-tip__tier--on': Number(rule.threshold) === activeThreshold }">
        <i v-if="Number(rule.threshold) === activeThreshold" class="recharge-campaign-tip__tier-flag">当前档</i>
        <span class="recharge-campaign-tip__tier-full">满 {{ formatNum(rule.threshold) }}</span>
        <span class="recharge-campaign-tip__tier-bonus">赠 <b>{{ formatNum(rule.bonus) }}</b></span>
      </div>
    </div>
    <div class="recharge-campaign-tip__status">
      <div class="recharge-campaign-tip__gain">
        <template v-if="estimate > 0">✓ 本次预计赠送 {{ formatNum(estimate) }} 算力</template>
        <template v-else>{{ guideText }}</template>
      </div>
      <div v-if="estimate > 0 && upsellText" class="recharge-campaign-tip__upsell">↗ {{ upsellText }}</div>
    </div>
    <div class="recharge-campaign-tip__note">仅按单笔最高档赠送</div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  props: {
    visible: { type: Boolean, default: false },
    computingPowerType: { type: Number, required: true },
    quantity: { type: [Number, String], default: 0 },
  },
  data() {
    return { campaign: null, now: Date.now(), timer: null, requestSeq: 0 }
  },
  computed: {
    rules() {
      if (!this.campaign || !this.campaign.end_at || this.now > Date.parse(this.campaign.end_at.replace(' ', 'T') + '+08:00')) return []
      return (this.campaign.tiers || []).filter(rule => rule.computing_power_type === this.computingPowerType)
        .sort((a, b) => Number(a.threshold) - Number(b.threshold))
    },
    estimate() {
      const quantity = Number(this.quantity)
      if (!Number.isFinite(quantity) || quantity <= 0) return 0
      return this.rules.reduce((bonus, rule) => quantity >= Number(rule.threshold) ? Number(rule.bonus) : bonus, 0)
    },
    maxBonus() {
      return this.rules.reduce((max, rule) => Math.max(max, Number(rule.bonus)), 0)
    },
    activeThreshold() {
      const quantity = Number(this.quantity)
      if (!Number.isFinite(quantity) || quantity <= 0) return 0
      return this.rules.reduce((threshold, rule) => quantity >= Number(rule.threshold) ? Number(rule.threshold) : threshold, 0)
    },
    guideText() {
      const first = this.rules[0]
      if (!first || this.estimate > 0) return ''
      const quantity = Number(this.quantity)
      if (Number.isFinite(quantity) && quantity > 0) {
        return `再充 ${this.formatNum(Math.ceil(Number(first.threshold) - quantity))} 即可享赠 ${this.formatNum(first.bonus)} 算力`
      }
      return `充 ${this.formatNum(first.threshold)} 即可享赠 ${this.formatNum(first.bonus)} 算力`
    },
    upsellText() {
      const quantity = Number(this.quantity)
      if (!Number.isFinite(quantity) || quantity <= 0) return ''
      const next = this.rules.find(rule => Number(rule.threshold) > quantity)
      if (!next) return ''
      return `再充 ${this.formatNum(Math.ceil(Number(next.threshold) - quantity))} 可享赠 ${this.formatNum(next.bonus)} 算力`
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        this.requestSeq += 1
        if (this.timer) window.clearInterval(this.timer)
        if (value) {
          this.now = Date.now()
          this.timer = window.setInterval(() => { this.now = Date.now() }, 1000)
          this.load(this.requestSeq)
        } else {
          this.campaign = null
        }
      },
    },
  },
  beforeDestroy() { if (this.timer) window.clearInterval(this.timer) },
  methods: {
    ...mapActions({ getRechargeCampaign: 'ucenter/getRechargeCampaign' }),
    formatNum(value) {
      const num = Number(value)
      return Number.isFinite(num) ? String(num) : String(value)
    },
    load(sequence) {
      this.campaign = null
      this.getRechargeCampaign({ data: {}, success: res => {
        if (this.visible && sequence === this.requestSeq && res.code === 200) this.campaign = res.data || null
      } }).catch(() => { if (sequence === this.requestSeq) this.campaign = null })
    },
  },
}
</script>

<style scoped>
.recharge-campaign-tip { margin: 12px 0; padding: 12px 14px; border: 1px solid #f6cf86; border-radius: 8px; background: #fff8e9; color: #75511b; font-size: 12px; line-height: 1.6; }
.recharge-campaign-tip__head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.recharge-campaign-tip__title { color: #9d5810; font-size: 14px; font-weight: 700; }
.recharge-campaign-tip__badge { flex-shrink: 0; padding: 2px 10px; border-radius: 10px; background: linear-gradient(135deg, #c8443c, #f07575); color: #fff; font-size: 11px; font-weight: 600; }
.recharge-campaign-tip__time { margin-top: 2px; font-size: 11px; color: rgba(117, 81, 27, .75); }
.recharge-campaign-tip__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 7px; margin-top: 9px; }
.recharge-campaign-tip__tier { position: relative; padding: 7px 4px 6px; border: 1px solid #f0dfba; border-radius: 7px; background: #fffdf6; text-align: center; box-sizing: border-box; }
.recharge-campaign-tip__tier--on { padding: 6px 3px 5px; border: 2px solid #c8443c; background: #fff5f5; }
.recharge-campaign-tip__tier-flag { position: absolute; top: -1px; right: -1px; padding: 0 5px; border-radius: 0 6px 0 7px; background: linear-gradient(135deg, #c8443c, #f07575); color: #fff; font-size: 10px; font-style: normal; line-height: 15px; }
.recharge-campaign-tip__tier-full { display: block; font-size: 11px; color: #8a6a2f; }
.recharge-campaign-tip__tier--on .recharge-campaign-tip__tier-full { color: #c8443c; font-weight: 600; }
.recharge-campaign-tip__tier-bonus { display: block; margin-top: 1px; font-size: 12px; }
.recharge-campaign-tip__tier-bonus b { color: #c45518; font-size: 13px; }
.recharge-campaign-tip__tier--on .recharge-campaign-tip__tier-bonus b { color: #c8443c; }
.recharge-campaign-tip__status { margin-top: 10px; }
.recharge-campaign-tip__gain { color: #c45518; font-weight: 700; }
.recharge-campaign-tip__upsell { margin-top: 3px; font-size: 11px; color: #b07a2a; }
.recharge-campaign-tip__note { margin-top: 8px; padding-top: 7px; border-top: 1px dashed rgba(157, 88, 16, .18); font-size: 11px; color: rgba(117, 81, 27, .72); }
</style>
